import * as React from 'react'
import {
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useMediaQuery,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import { Box } from '@mui/system'

import DefaultScreenSizeTheme from 'assets/styles/themes/DefaultScreenSizeTheme'
import {
  sxDialog,
  sxDialogTitle,
  sxCloseIcon,
  sxPrimaryActionBtn,
  sxTwoActionBtn,
} from 'components/Shared/Dialog/DialogStyles'

type CustomDialogProps = {
  title: string
  content: React.ReactElement
  showCloseBtn?: boolean
  primaryBtn?: React.ReactElement
  secondaryBtn?: React.ReactElement
  isModalOpen: boolean
  enableBackdropClick?: boolean
  enableActionButtons?: boolean
  onCloseModal: () => void
  sxDialogProps?: object
  testProps?: any
  isTestPropsEnabled?: boolean
}

export interface DialogTitleProps {
  children?: React.ReactNode
  onClose: () => void
  closeBtnToggle: boolean
}

const BootstrapDialogTitle = (props: DialogTitleProps) => {
  const { children, onClose, closeBtnToggle } = props

  return (
    <DialogTitle sx={{ ...sxDialogTitle }}>
      <Box display="flex" alignItems="center">
        <Box flexGrow={1} data-testid="customDialog-title">
          <Typography variant="h5"> {children}</Typography>
        </Box>
        {closeBtnToggle && (
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ ...sxCloseIcon }}
            data-testid="close-button"
          >
            <CloseIcon sx={{ float: 'right' }} />
          </IconButton>
        )}
      </Box>
    </DialogTitle>
  )
}

const CustomDialog = ({
  title,
  content,
  showCloseBtn,
  primaryBtn,
  secondaryBtn,
  isModalOpen,
  enableBackdropClick,
  enableActionButtons,
  onCloseModal,
  testProps, // add this only when to view the modal in test cases writing
  isTestPropsEnabled,
}: CustomDialogProps) => {
  const { t } = useTranslation()
  const fullScreenBreakpoint = useMediaQuery(
    DefaultScreenSizeTheme.breakpoints.down('sm'),
  )

  return (
    <Dialog
      onClose={() => {
        enableBackdropClick && onCloseModal()
      }}
      fullScreen={fullScreenBreakpoint}
      aria-labelledby="customized-dialog"
      open={isModalOpen}
      PaperProps={{
        sx: { ...sxDialog },
      }}
      disableEnforceFocus
      {...(isTestPropsEnabled && testProps)}
      data-testid="dialog-component"
    >
      <BootstrapDialogTitle
        onClose={onCloseModal}
        closeBtnToggle={!!showCloseBtn}
      >
        {t(title)}
      </BootstrapDialogTitle>
      <DialogContent sx={{ padding: '0rem 1.5rem 0rem' }}>
        <Typography variant="body1" sx={{ color: '#000000' }}>
          {content}
        </Typography>
      </DialogContent>

      {enableActionButtons && (!!secondaryBtn || !!primaryBtn) && (
        <DialogActions
          data-testid="customDialog-actionButtonGroup"
          sx={{
            ...(!!primaryBtn && !!secondaryBtn && sxTwoActionBtn),
            ...(!!primaryBtn && !secondaryBtn && sxPrimaryActionBtn),
          }}
        >
          {secondaryBtn}
          {primaryBtn}
        </DialogActions>
      )}
    </Dialog>
  )
}

export default CustomDialog
